import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Radio,
  Lock,
  Sparkles,
  ArrowUpRight,
  Calendar,
  Video,
  Crown,
  CheckCircle2,
  Shield,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import ShareButtons from "@/components/ShareButtons";
import { HERO_NINA as HERO_IMG } from "@/lib/assets";
import { ProVaultPanel } from "@/pages/ProVault";

export default function Backstage() {
  const [params, setParams] = useSearchParams();
  const initialEmail = params.get("email") || "";
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [access, setAccess] = useState(null); // null | { access:true, join_url, ... } | { access:false, ... }
  const [events, setEvents] = useState([]);

  // Load upcoming backstage events
  useEffect(() => {
    api
      .get("/public/backstage/events")
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]));
  }, []);

  const submit = useCallback(
    async (e, overrideEmail) => {
      if (e) e.preventDefault();
      const tgt = (overrideEmail || email).trim();
      if (!tgt) return;
      setBusy(true);
      try {
        const { data } = await api.post("/public/backstage/access", { email: tgt });
        setAccess(data);
        if (data.access) {
          toast.success("Access granted. The stage is yours.");
          // Persist email in URL for reload
          if (!initialEmail) {
            const np = new URLSearchParams(params);
            np.set("email", tgt);
            setParams(np, { replace: true });
          }
        } else {
          toast.message("Upgrade to a paid Inner Circle membership to join Backstage.");
        }
      } catch (err) {
        toast.error(formatApiError(err) || "Couldn't check access. Try again.");
      } finally {
        setBusy(false);
      }
    },
    [email, initialEmail, params, setParams],
  );

  // Auto-attempt access if email arrives via URL (from email blast deep-link).
  // Deferred a tick so the state updates land outside the synchronous effect.
  useEffect(() => {
    if (!initialEmail) return undefined;
    const t = setTimeout(() => submit(undefined, initialEmail), 0);
    return () => clearTimeout(t);
  }, [initialEmail, submit]);

  const reset = useCallback(() => {
    setAccess(null);
    setEmail("");
    const np = new URLSearchParams(params);
    np.delete("email");
    setParams(np, { replace: true });
  }, [params, setParams]);

  return (
    <div data-testid="backstage-page">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Backstage Access — Live with Nina Capone"
          className="absolute inset-0 w-full h-full object-cover object-[60%_30%] opacity-50"
        />
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, rgba(5,3,8,0.45) 0%, rgba(5,3,8,0.95) 95%)",
          }}
        />
        <div className="relative max-w-[1300px] mx-auto px-6 lg:px-12 py-24 w-full">
          <div className="max-w-3xl space-y-6 fade-up">
            <SectionLabel number="06" label="Inner Circle · Live Shows" />
            <h1 className="font-serif font-light tracking-tighter text-[2.6rem] sm:text-6xl lg:text-8xl leading-[0.95]">
              <em className="italic" style={{ color: "var(--gold)" }}>
                Backstage
              </em>
              {" "}Access.
            </h1>
            <p className="text-white/75 text-base sm:text-lg max-w-2xl leading-relaxed">
              Live with Nina, off-script, on Be.Live. Inner Circle paid members
              walk in. No password — just the email on your membership.
            </p>
            <div className="flex items-center gap-3 pt-1" data-testid="backstage-share">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Share</span>
              <ShareButtons
                shareUrl={`${window.location.origin}/api/og/page/backstage`}
                canonicalUrl={`${window.location.origin}/backstage`}
                text="Backstage · Live with Nina Capone"
                testidPrefix="backstage-share"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ACCESS GATE */}
      <section className="py-16 lg:py-20 border-t border-white/5" data-testid="backstage-gate">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            <SectionLabel label="Tap In" />
            <h2 className="font-serif font-light tracking-tighter text-3xl lg:text-4xl leading-[1.05]">
              Enter the email on your{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                membership.
              </em>
            </h2>
            <p className="text-white/65 text-sm leading-relaxed max-w-md">
              Same email you used to subscribe. If you&apos;re active, the join link
              drops here. If you&apos;re free, you&apos;ll see the upgrade path.
            </p>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/45">
              <Shield size={11} style={{ color: "var(--gold)" }} />
              No password. Email-only check. Stripe-verified.
            </div>
          </div>

          <div className="lg:col-span-7">
            {access === null && (
              <form
                onSubmit={submit}
                className="surface p-8 lg:p-10 space-y-5"
                data-testid="backstage-form"
              >
                <Label htmlFor="bs-email" className="eyebrow">
                  Your Membership Email
                </Label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    id="bs-email"
                    type="email"
                    required
                    placeholder="you@yourdomain.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 flex-1 h-12"
                    data-testid="backstage-email"
                  />
                  <Button
                    type="submit"
                    disabled={busy}
                    className="btn-gold rounded-none px-6 h-12 text-xs inline-flex items-center justify-center gap-2"
                    data-testid="backstage-submit"
                  >
                    {busy ? "Checking…" : "Get My Backstage Link"}
                    <ArrowUpRight size={14} />
                  </Button>
                </div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/35 leading-relaxed">
                  Backstage is a paid Inner Circle perk. Not a member yet?{" "}
                  <Link
                    to="/fan-club"
                    className="text-[color:var(--gold)] hover:underline"
                    data-testid="backstage-upgrade-link"
                  >
                    Become one for $20/mo
                  </Link>
                  .
                </p>
              </form>
            )}

            {access && access.access && (
              <div
                className="surface p-8 lg:p-10 space-y-6 ring-1 ring-[color:var(--gold)]/40"
                style={{ background: "rgba(232,180,184,0.04)" }}
                data-testid="backstage-granted"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={22} style={{ color: "var(--gold)" }} />
                  <p className="eyebrow" style={{ color: "var(--gold)" }}>
                    Access Granted
                  </p>
                </div>
                <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light leading-tight">
                  {access.name ? `Welcome back, ${access.name.split(" ")[0]}.` : "You're in."}
                </h3>
                {access.next_event && (
                  <div className="text-sm text-white/65">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">
                      Next Live Show
                    </p>
                    <p>
                      <strong style={{ color: "var(--gold)" }}>
                        {access.next_event.title}
                      </strong>{" "}
                      · {access.next_event.date}
                      {access.next_event.time ? ` · ${access.next_event.time}` : ""}
                    </p>
                  </div>
                )}
                <a
                  href={access.join_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold inline-flex items-center gap-2 px-7 py-4 text-xs"
                  data-testid="backstage-join-now"
                >
                  <Video size={14} />
                  Join Live · Be.Live
                  <ArrowUpRight size={14} />
                </a>
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/45">
                  This is your one-tap entry. Bookmark this page; it auto-reveals when you visit.
                </p>
                <button
                  type="button"
                  onClick={reset}
                  className="text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-white"
                  data-testid="backstage-reset"
                >
                  Not you? Use a different email.
                </button>
              </div>
            )}

            {access && !access.access && (
              <div
                className="surface p-8 lg:p-10 space-y-5"
                data-testid="backstage-denied"
              >
                <div className="flex items-center gap-3">
                  <Lock size={20} style={{ color: "rgba(255,255,255,0.5)" }} />
                  <p className="eyebrow text-white/55">Paid Membership Required</p>
                </div>
                <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light leading-tight">
                  Backstage is for{" "}
                  <em className="italic" style={{ color: "var(--gold)" }}>
                    paid members.
                  </em>
                </h3>
                <p className="text-white/65 leading-relaxed">{access.message}</p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link
                    to="/fan-club"
                    className="btn-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                    data-testid="backstage-upgrade-cta"
                  >
                    <Crown size={14} />
                    Upgrade · $20/mo
                  </Link>
                  <button
                    type="button"
                    onClick={reset}
                    className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                    data-testid="backstage-try-other"
                  >
                    Try a different email
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* UPCOMING BACKSTAGE EVENTS */}
      <section className="py-16 lg:py-20 border-t border-white/5" data-testid="backstage-events">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-10 gap-3 flex-wrap">
            <div>
              <SectionLabel label="On The Calendar" />
              <h2 className="font-serif text-3xl lg:text-5xl tracking-tighter font-light leading-tight">
                Upcoming{" "}
                <em className="italic" style={{ color: "var(--gold)" }}>
                  live shows.
                </em>
              </h2>
            </div>
          </div>
          {events.length === 0 ? (
            <p className="text-white/55 text-sm py-6">
              No backstage shows on the calendar yet. Stay tuned — when Nina goes
              live, your inbox will know.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {events.map((ev) => (
                <div
                  key={ev.id}
                  className="surface p-6 lg:p-7 space-y-3"
                  data-testid={`backstage-event-${ev.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] border px-2 py-1"
                      style={{ color: "var(--gold)", borderColor: "rgba(232,180,184,0.5)" }}
                    >
                      <Radio size={11} />
                      Backstage · Live
                    </span>
                    {ev.status === "past" && (
                      <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                        Past
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-2xl tracking-tighter font-light leading-tight">
                    {ev.title}
                  </h3>
                  <p className="text-sm text-white/65 inline-flex items-center gap-2">
                    <Calendar size={14} style={{ color: "var(--gold)" }} />
                    {ev.date}
                    {ev.time ? ` · ${ev.time}` : ""}
                  </p>
                  {ev.description && (
                    <p className="text-sm text-white/55 leading-relaxed line-clamp-3">
                      {ev.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-5">
          {[
            { i: Sparkles, t: "Be.Live Stream", c: "Nina goes live straight from Be.Live. You get a direct guest link — no app required." },
            { i: Lock,     t: "Members Only",   c: "Only active Inner Circle paid members ($20/mo) see the join button. Free members get the upgrade path." },
            { i: Video,    t: "Bookmark It",    c: "Bookmark this page once you've confirmed your email. We remember you next time you visit." },
          ].map((b) => (
            <div key={b.t} className="surface p-6 space-y-3">
              <b.i size={18} style={{ color: "var(--gold)" }} />
              <h3 className="font-serif text-xl tracking-tighter font-light">{b.t}</h3>
              <p className="text-sm text-white/55 leading-relaxed">{b.c}</p>
            </div>
          ))}
        </div>
      </section>

      {/* THE PRO VAULT — separate membership for industry pros */}
      <section
        id="pro-vault"
        className="py-16 lg:py-24 border-t border-white/5 provault-bg relative overflow-hidden"
        data-testid="backstage-pro-vault"
      >
        <div className="absolute inset-0 provault-glow pointer-events-none" />
        <div className="relative">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 mb-2">
            <SectionLabel number="07" label="A Separate Membership · $6.50/mo" />
          </div>
          <ProVaultPanel />
        </div>
      </section>
    </div>
  );
}
